import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { Plane, ArrowLeft, Shield, Zap, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase/client';
import { useAuthContext } from '../../contexts/AuthContext';

export function SignInPage() {
  const { user, loading } = useAuthContext();
  const navigate = useNavigate();
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex p-4 bg-gradient-to-r from-primary-500/20 to-blue-500/20 backdrop-blur-sm rounded-3xl mb-4">
            <div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-white/80">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const benefits = [
    {
      icon: Shield,
      title: 'Secure & Trusted',
      description: 'Enterprise-grade security for your business data'
    },
    {
      icon: Zap,
      title: 'Instant Setup',
      description: 'Get your services listed in minutes'
    },
    {
      icon: Users,
      title: 'Global Network',
      description: 'Connect with aviation professionals worldwide'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=2000')] bg-cover bg-center opacity-5"></div>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-40 h-40 sm:w-80 sm:h-80 bg-primary-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 sm:w-80 sm:h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex">
        {/* Left side - Benefits */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-12 xl:p-16">
          <div className="max-w-lg">
            <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            
            <div className="flex items-center gap-3 mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-blue-500 rounded-xl blur-lg opacity-60"></div>
                <div className="relative bg-gradient-to-r from-primary-500 to-blue-600 p-3 rounded-xl shadow-lg">
                  <Plane className="h-8 w-8 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
                  AirPortal
                </h1>
                <p className="text-white/60 text-sm">Aviation Services Marketplace</p>
              </div>
            </div>

            <h2 className="text-4xl font-bold text-white mb-6 leading-tight">
              Welcome back to the future of aviation services
            </h2>
            
            <p className="text-white/70 text-lg mb-12 leading-relaxed">
              Join thousands of aviation professionals who trust AirPortal to grow their business 
              and connect with clients worldwide.
            </p>

            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-3 bg-gradient-to-r from-primary-500/20 to-blue-500/20 backdrop-blur-sm rounded-xl">
                    <benefit.icon className="h-6 w-6 text-primary-300" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-white/70 text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Sign in form */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center p-6 sm:p-12">
          <div className="w-full max-w-md mx-auto">
            {/* Mobile header */}
            <div className="lg:hidden text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-3 mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-blue-500 rounded-xl blur-lg opacity-60"></div>
                  <div className="relative bg-gradient-to-r from-primary-500 to-blue-600 p-3 rounded-xl shadow-lg">
                    <Plane className="h-6 w-6 text-white" />
                  </div>
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
                  AirPortal
                </span>
              </Link>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-3xl blur-xl"></div>
              <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/20 p-8 sm:p-10">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-white mb-2">
                    Welcome back
                  </h2>
                  <p className="text-white/70">
                    Don't have an account?{' '}
                    <Link 
                      to="/signup"
                      className="text-primary-300 hover:text-primary-200 font-medium transition-colors"
                    >
                      Create one here
                    </Link>
                  </p>
                </div>

                <Auth
                  supabaseClient={supabase}
                  appearance={{ 
                    theme: ThemeSupa,
                    variables: {
                      default: {
                        colors: {
                          brand: '#008080',
                          brandAccent: '#006666',
                          inputBackground: 'rgba(255, 255, 255, 0.1)',
                          inputBorder: 'rgba(255, 255, 255, 0.2)',
                          inputBorderHover: '#008080',
                          inputBorderFocus: '#008080',
                          inputText: 'white',
                          inputPlaceholder: 'rgba(255, 255, 255, 0.6)',
                        }
                      },
                    },
                    className: {
                      container: 'w-full',
                      button: 'w-full bg-gradient-to-r from-primary-500 to-blue-600 hover:from-primary-400 hover:to-blue-500 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none border-0',
                      input: 'w-full rounded-xl border border-white/20 px-4 py-4 bg-white/10 backdrop-blur-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200',
                      label: 'block text-sm font-medium text-white/90 mb-2',
                      message: 'mt-3 text-sm text-red-300 bg-red-500/20 backdrop-blur-sm px-4 py-3 rounded-xl border border-red-500/30',
                      anchor: 'hidden',
                    }
                  }}
                  providers={[]}
                  view={showForgotPassword ? 'forgotten_password' : 'sign_in'}
                  redirectTo={`${window.location.origin}/dashboard`}
                  localization={{
                    variables: {
                      sign_in: {
                        email_label: 'Email address',
                        password_label: 'Password',
                        button_label: 'Sign in to your account',
                        loading_button_label: 'Signing in...',
                        link_text: '',
                      },
                      forgotten_password: {
                        button_label: 'Send reset instructions',
                        confirmation_text: 'Check your email for the password reset link',
                        email_label: 'Email address',
                        password_label: 'New password',
                        link_text: '',
                      }
                    }
                  }}
                />

                <div className="mt-6 flex flex-col gap-3">
                  {!showForgotPassword ? (
                    <button
                      onClick={() => setShowForgotPassword(true)}
                      className="text-center text-sm text-primary-300 hover:text-primary-200 transition-colors"
                    >
                      Forgot your password?
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowForgotPassword(false)}
                      className="text-center text-sm text-primary-300 hover:text-primary-200 transition-colors"
                    >
                      Back to sign in
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile benefits */}
            <div className="lg:hidden mt-12 space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 text-center justify-center">
                  <benefit.icon className="h-5 w-5 text-primary-300" />
                  <span className="text-white/70 text-sm">{benefit.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}